import { DefaultButton, DetailsList, IColumn, Icon, Link, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { EntityAPI, EntityType, IAIPropertyDefinition, IEntityColumn } from '../../../API';
import { IUserConfigPackage } from '../../../API';
import AIPropertyEditor, { AIPropertyEditorState } from './AIPropertyEditor';

export class AIPropertiesListState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: AIPropertyEditorState;

	public ids: string[] = [];
	
	update?: (ids: string[]) => void;
	
	items: IAIPropertyDefinition[] = [];
	availableFields: IEntityColumn[] = [];

	constructor(userConfig: IUserConfigPackage, update?:(ids:string[]) => void) {
		this.userConfig = userConfig;
		this.update = update;

		this.editState = new AIPropertyEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this));

		makeAutoObservable(this);
	}
	
	setAvailableFields(listUrlColumns: IEntityColumn[]) {
		this.availableFields = listUrlColumns;
	}

	public setSelected(ids:string[]) {
		this.ids = ids || [];
	}

	public async load(): Promise<void> {
		this.loading = true;

		if(this.ids.length > 0) {
			let api = await this.getApi();
			let items = await api.items(this.ids, null);
			this.setItems(items);
		} else {
			this.setItems([]);
		}
	}

	private async getApi() : Promise<EntityAPI<IAIPropertyDefinition>> {
		return await EntityAPI.fromEntityType(
			EntityType.AIPropertyDefinition,
			this.userConfig,
		) as EntityAPI<IAIPropertyDefinition>;
	}

	ensureInitialLoaded() {
		if (!this.loading && !this.loaded) {
			this.load();
		}
	}

	setItems(items: IAIPropertyDefinition[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: IAIPropertyDefinition) {
		if(this.editState) {
			this.editState.setColumns(this.availableFields);
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {

		this.edit({
			FieldName: '',
			Prompt: '',
			WorkspacePath: '',
			Expression: '',

			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
		});
	}

	public async save(original:IAIPropertyDefinition, updated:IAIPropertyDefinition) : Promise<void> {

		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:IAIPropertyDefinition) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(item:IAIPropertyDefinition) {
		this.items = this.items.filter(p => p !== item);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		if(this.update) {
			this.update(this.items.map(p => p.Id));
		}
	}
}

function AIPropertiesList({ state }: { state: AIPropertiesListState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	let columns: IColumn[] = [
		{
			key: 'field',
			name: 'Field',
			minWidth: 100,
			maxWidth: 100,
			onRender: (item: IAIPropertyDefinition) => <Link onClick={() => {
				state.edit(item);
			}}>{item.FieldName}</Link>,
		},
		{
			key: 'prompt',
			name: 'Prompt',
			minWidth: 400,
			maxWidth: 400,
			onRender: (item: IAIPropertyDefinition) => <span title={item.Prompt}>{item.Prompt}</span>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 100,
			onRender: (item: IAIPropertyDefinition) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<>
			<>
				{state.editState && <AIPropertyEditor state={state.editState} /> }

				{(state?.items && state.items.length) ?
					<DetailsList selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
			</>

			<div style={{marginTop:20}}>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add</DefaultButton>
			</div>
		</>
	);
}

export default observer(AIPropertiesList);
